import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import Check from '../../icons/Check'
import Info from '../../icons/Info'
import WarningDiamond from '../../icons/WarningDiamond'
import WarningTriangle from '../../icons/WarningTriangle'
import { cn } from '../../utils/cn'

const alertVariants = cva(
  'relative w-full rounded-lg p-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-5 has-[[data-slot=alert-title]]:[&>svg]:translate-y-px [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive: 'text-destructive-foreground bg-destructive',
        warning: 'text-warning-foreground bg-warning',
        info: 'bg-info text-info-foreground',
        success: 'bg-success text-success-foreground',
        primary: 'bg-primary text-primary-foreground',
        muted: 'bg-muted text-muted-foreground'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>

const variantIcons: Partial<Record<AlertVariant, React.ComponentType<{ className?: string }>>> = {
  info: Info,
  success: Check,
  warning: WarningTriangle,
  destructive: WarningDiamond
}

function renderDefaultIcon(variant: AlertVariant): React.ReactNode {
  const IconComponent = variantIcons[variant]
  return IconComponent ? <IconComponent /> : null
}

interface AlertProps extends React.ComponentProps<'div'>, VariantProps<typeof alertVariants> {
  /**
   * Icon rendered to the left of the alert content.
   *
   * - `undefined` (default): use the icon associated with the variant — `info` → info
   *   circle, `success` → check, `warning` → triangle warning, `destructive` → diamond
   *   warning. Variants without a default (`default`, `primary`, `muted`) render no icon.
   * - A React node: overrides the default with that node.
   * - `null` or `false`: render no icon, even if the variant has a default.
   */
  icon?: React.ReactNode | null | false
}

function Alert({ className, variant, icon, children, ...props }: AlertProps) {
  const renderedIcon = icon === undefined ? renderDefaultIcon(variant ?? 'default') : icon

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {renderedIcon}
      {children}
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 text-sm', className)}
      {...props}
    />
  )
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn('col-start-2 grid justify-items-start gap-1 text-xs', className)}
      {...props}
    />
  )
}

export { Alert, alertVariants, AlertDescription, AlertTitle }
